import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import $ from 'jquery';
import 'datatables.net-dt/js/dataTables.dataTables.js';
import 'datatables.net-responsive-dt/js/responsive.dataTables.js';
import 'datatables.net-responsive-dt/css/responsive.dataTables.css';
import { API_URL, LOCATION_ID, TOKEN } from '..';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

const InvoiceListLayer = ({ jobsData = [] }) => {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [paymentLogs, setPaymentLogs] = useState([]);
  const [tableError, setTableError] = useState('');
  const [selectedStatusFor, setSelectedStatusFor] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [receivedAmount, setReceivedAmount] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('CONFIRM');
  const [showModal, setShowModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [expandedMobileCards, setExpandedMobileCards] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [cityList, setCityList] = useState([]);

  const filteredJobsData = useMemo(() => {
    let baseData = jobsData || [];
    
    // Filter by user role if not admin
    const user = JSON.parse(localStorage.getItem('user'));
    const isAdmin = user?.roles?.role === 'admin' || user?.role === 'admin';
    if (!isAdmin && user?.id) {
      baseData = baseData.filter(invoice => 
        String(invoice.added_by) === String(user.id) || 
        (user.crm_id && String(invoice.added_by) === String(user.crm_id))
      );
    }

    const query = searchQuery.trim().toLowerCase();
    if (!query) return baseData;
    
    return baseData.filter((invoice) => {
      const cityName = invoice?.city?.name || cityList.find((c) => c.id === invoice?.cityId || String(c.id) === String(invoice?.cityId))?.name || '';
      const creatorName = invoice?.creator?.name || '';
      const searchable = [
        invoice?.invoice_number,
        invoice?.customer_name,
        invoice?.status,
        invoice?.totalAmount,
        invoice?.amount,
        cityName,
        creatorName,
      ].filter(Boolean).join(' ').toLowerCase();
      return searchable.includes(query);
    });
  }, [jobsData, searchQuery, cityList]);

  const toggleMobileDetails = (invoiceId) => {
    setExpandedMobileCards((prev) => ({
      ...prev,
      [invoiceId]: !prev[invoiceId],
    }));
  };

  const privileges = useMemo(() => {
    const storedPrivileges = localStorage.getItem('privilages');
    return storedPrivileges
      ? JSON.parse(storedPrivileges)
      : { invoice: { edit: false, delete: false, read: false, changeStatus: false } };
  }, []);

  const canRead = currentUser?.roles?.role === 'admin' || privileges?.invoice?.read;
  const canEdit = currentUser?.roles?.role === 'admin' || privileges?.invoice?.edit;
  const canDelete = currentUser?.roles?.role === 'admin' || privileges?.invoice?.delete;

  const formatDateString = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '-';
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  const formatMoney = (invoice) => {
    const currency = invoice?.currency || '';
    const amount = Number(invoice?.totalAmount ?? invoice?.amount ?? 0);
    return `${currency} ${amount.toFixed(2)}`;
  };

  const getStatusMeta = (status) => {
    const normalized = (status || '').toLowerCase();

    if (normalized === 'paid') {
      return {
        label: 'Paid',
        className: 'status-badge status-paid',
        bsClass: 'success',
      };
    }

    if (normalized === 'partially-paid') {
      return {
        label: 'Partially Paid',
        className: 'status-badge status-partial',
        bsClass: 'warning',
      };
    }

    if (normalized === 'un-paid' || normalized === 'unpaid') {
      return {
        label: 'Unpaid',
        className: 'status-badge status-unpaid',
        bsClass: 'danger',
      };
    }

    return {
      label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
      className: 'status-badge status-default',
      bsClass: 'secondary',
    };
  };

  const fetchAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/accounts/location/${LOCATION_ID}`, {
        method: 'GET',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (response.status === 200) {
        if (!Array.isArray(data.accounts)) {
          throw new Error('Invalid data format: accounts is not an array');
        }

        setAccounts(data.accounts || []);
        setTableError('');
      } else {
        throw new Error(data.message || 'Failed to fetch accounts');
      }
    } catch (err) {
      console.error('Fetch accounts error:', err);
      setTableError(err.message || 'Unable to load accounts. Please try again.');
      setAccounts([]);

      toast.error(err.message || 'Unable to fetch accounts', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const fetchPaymentLogs = async (invoiceId) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/invoice/${invoiceId}`, {
        method: 'GET',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Failed to fetch logs:', data.message);
        setPaymentLogs([]);
        return;
      }

      const logsWithUsers = await Promise.all(
        data.map(async (log) => {
          try {
            const options = {
              method: 'GET',
              url: `https://services.leadconnectorhq.com/users/${log.user_id}`,
              headers: {
                Authorization: `Bearer ${TOKEN}`,
                Version: '2021-07-28',
                Accept: 'application/json',
              },
            };

            const { data: userData } = await axios.request(options);

            return {
              ...log,
              userName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Unknown User',
            };
          } catch (err) {
            console.error('User fetch error:', err);
            return { ...log, userName: 'Unknown User' };
          }
        })
      );

      setPaymentLogs(logsWithUsers);
    } catch (error) {
      console.error('Error fetching payment logs:', error);
      setPaymentLogs([]);
    }
  };

  const openPaymentModal = async (invoice, statusFor = null) => {
    setSelectedInvoice(invoice);
    setSelectedStatusFor(statusFor);
    setSelectedAccount(null);
    setReceivedAmount('');
    setPaymentStatus('CONFIRM');
    await fetchAccounts();
    await fetchPaymentLogs(invoice?.id);
    setShowModal(true);
  };

  const closePaymentModal = () => {
    setShowModal(false);
    setSelectedInvoice(null);
    setSelectedStatusFor(null);
    setSelectedAccount(null);
    setReceivedAmount('');
    setPaymentStatus('CONFIRM');
    setPaymentLogs([]);
  };

  const handleDelete = async (id) => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      navigate('/sign-in');
      return;
    }

    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Delete order form?',
      text: 'This action cannot be undone.',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#111827',
    });

    if (!confirm.isConfirmed) return;

    try {
      const response = await fetch(`${API_URL}/api/invoice/delete-invoice/${id}`, {
        method: 'DELETE',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.message === 'Invoice deleted successfully') {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Order form deleted successfully!',
          confirmButtonColor: '#111827',
        });
        window.location.reload();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.message || 'Failed to delete order form',
          confirmButtonColor: '#111827',
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to delete order form: ${error.message}`,
        confirmButtonColor: '#111827',
      });
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      navigate('/sign-in');
      return;
    }

    try {
      if (newStatus === 'paid' || newStatus === 'partially-paid') {
        const invoice = jobsData.find((job) => job.id === id);
        await openPaymentModal(invoice, newStatus);
        return;
      }

      const response = await fetch(`${API_URL}/api/invoice/update-status/${id}`, {
        method: 'PUT',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();

      if (data.message === 'Invoice successfully updated') {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Order form status updated successfully!',
          confirmButtonColor: '#111827',
        });
        window.location.reload();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.message || 'Failed to update order form status',
          confirmButtonColor: '#111827',
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to update order form status: ${error.message}`,
        confirmButtonColor: '#111827',
      });
    }
  };

  const handleSavePayment = async () => {
    if (!selectedAccount || !receivedAmount || !selectedInvoice) {
      toast.error('Please fill all required fields', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    const totalAmount = parseFloat(selectedInvoice.amount || selectedInvoice.totalAmount || 0);
    const alreadyPaid = paymentLogs.reduce((sum, log) => sum + parseFloat(log.received_amount || 0), 0);
    const remainingBeforeThisPayment = totalAmount - alreadyPaid;
    const received = parseFloat(receivedAmount);

    if (Number.isNaN(received) || received <= 0) {
      toast.error('Please enter a valid received amount', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    if (received > remainingBeforeThisPayment) {
      toast.error('Received amount cannot exceed remaining balance', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    try {
      const payload = {
        account_id: selectedAccount.value,
        user_id: currentUser.id,
        payment_status: paymentStatus,
        total_amount: totalAmount,
        selected_invoice_id: selectedInvoice.id,
        received_amount: received,
      };

      const response = await fetch(`${API_URL}/api/payments`, {
        method: 'POST',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.status === 201) {
        toast.success('Payment saved successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });

        const newStatus =
          selectedStatusFor ||
          (received >= remainingBeforeThisPayment ? 'paid' : 'partially-paid');

        await fetch(`${API_URL}/api/invoice/update-status/${selectedInvoice?.id}`, {
          method: 'PUT',
          headers: {
            Accept: '*/*',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: newStatus }),
        });

        window.location.reload();
      } else {
        toast.error(data.message || 'Failed to save payment', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error(`Failed to save payment: ${error.message}`, {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));

    if (!user) {
      navigate(`/sign-in?id=${LOCATION_ID}&tk_id=${TOKEN}`);
      return;
    }

    setCurrentUser(user);

    // Fetch cities for display in table
    fetch(`${API_URL}/api/city`)
      .then((r) => r.json())
      .then((data) => setCityList(data))
      .catch(() => {});
  }, [navigate]);

  useEffect(() => {
    if (!jobsData || jobsData.length === 0) return;

    if ($.fn.DataTable.isDataTable('#dataTable')) {
      $('#dataTable').DataTable().clear().destroy();
      $('#dataTable tbody').off('click');
      $('#dataTable tbody').off('change');
    }

    const getActionsColumn = (data, type, row) => {
      const user = JSON.parse(localStorage.getItem('user'));
      const storedPrivileges = localStorage.getItem('privilages');
      const currentPrivileges = storedPrivileges
        ? JSON.parse(storedPrivileges)
        : { invoice: { edit: false, delete: false, read: false } };

      const isAdmin = user?.roles?.role === 'admin';
      const actions = [];

      if (isAdmin || currentPrivileges?.invoice?.read) {
        actions.push(`<li><a style="cursor:pointer" class="dropdown-item view-btn" data-id="${row.id}"><i class="ph ph-eye me-2"></i>View Order Form</a></li>`);
        actions.push(`<li><a style="cursor:pointer" class="dropdown-item payment-btn" data-id="${row.id}"><i class="ph ph-receipt me-2"></i>View Payments</a></li>`);

        if (row.status !== 'paid') {
          actions.push(`<li><a style="cursor:pointer" class="dropdown-item log-payment" data-id="${row.id}"><i class="ph ph-credit-card me-2"></i>Make Payment</a></li>`);
        }
      }

      if (isAdmin || currentPrivileges?.invoice?.edit) {
        actions.push(`<li><a style="cursor:pointer" class="dropdown-item edit-btn" data-id="${row.id}"><i class="ph ph-pencil me-2"></i>Edit Order Form</a></li>`);
      }

      if (isAdmin || currentPrivileges?.invoice?.delete) {
        actions.push(`<li><a style="cursor:pointer" class="dropdown-item delete-btn text-danger" data-id="${row.id}"><i class="ph ph-trash me-2"></i>Delete Order Form</a></li>`);
      }

      if (actions.length === 0) return '<span class="text-muted small">No actions</span>';

      return `
        <div class="dropdown invoice-actions-dropdown">
          <button class="invoice-action-toggle dropdown-toggle" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport" data-bs-display="dynamic" aria-expanded="false">
            Actions
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            ${actions.join('')}
          </ul>
        </div>
      `;
    };

    const getStatusColumn = (data) => {
      const meta = getStatusMeta(data);
      return `<span class="badge bg-${meta.bsClass}">${meta.label}</span>`;
    };

    const table = $('#dataTable').DataTable({
      data: filteredJobsData,
      responsive: true,
      scrollX: false,
      autoWidth: false,
      pageLength: 10,
      order: [[0, 'desc']],
      dom: 'rt<"invoice-datatable-footer"lip>',
      language: {
        search: '',
        searchPlaceholder: 'Search order forms...',
      },
    columnDefs: [
  { responsivePriority: 1, targets: 0 },
  { responsivePriority: 2, targets: 1 },
  { responsivePriority: 3, targets: 6 },
  { responsivePriority: 4, targets: 4 },
  { responsivePriority: 5, targets: 5 },
],
      columns: [
        // { data: 'id', width: '70px' },
       {
  data: 'invoice_number',
  width: '140px',
  render: (data, type, row) => `
    <a href="/invoice-preview/${row.id}" class="fw-bold text-decoration-underline">
      ${data || `Order Form #${row.id}`}
    </a>
  `,
},
        { data: 'customer_name', width: '190px' },
        {
          data: 'issue_date',
          width: '130px',
          render: (data) => `<span>${formatDateString(data)}</span>`,
        },
        {
          data: 'due_date',
          width: '130px',
          render: (data) => `<span>${formatDateString(data)}</span>`,
        },
        {
          data: 'totalAmount',
          width: '130px',
          render: (data, type, row) => `<span>${row.currency || ''} ${parseFloat(row.amount || 0).toFixed(2)}</span>`,
        },
        {
          data: 'status',
          width: '120px',
          render: getStatusColumn,
        },
        // {
        //   data: 'cityId',
        //   width: '110px',
        //   title: 'City',
        //   render: (data, type, row) => {
        //     const cityName = row?.city?.name || cityList.find((c) => c.id === data || String(c.id) === String(data))?.name;
        //     return cityName ? `<span class="badge bg-light text-dark border">${cityName}</span>` : '<span class="text-muted small">—</span>';
        //   },
        // },
        {
          data: null,
          render: getActionsColumn,
          title: 'Actions',
          orderable: false,
          width: '120px',
        },
      ],
    });

    $('#dataTable tbody').on('click', '.edit-btn', function () {
      navigate(`/invoice-edit/${$(this).data('id')}`);
    });

    $('#dataTable tbody').on('click', '.view-btn', function () {
      navigate(`/invoice-preview/${$(this).data('id')}`);
    });

    $('#dataTable tbody').on('click', '.delete-btn', function () {
      handleDelete($(this).data('id'));
    });

    $('#dataTable tbody').on('click', '.log-payment', function () {
      const id = $(this).data('id');
      const invoice = jobsData.find((job) => job.id === id);
      openPaymentModal(invoice);
    });

    $('#dataTable tbody').on('click', '.payment-btn', function () {
      navigate(`/invoice-payments/${$(this).data('id')}`);
    });

    $('#dataTable tbody').on('change', '.status-dropdown', function () {
      handleStatusChange($(this).data('id'), $(this).val());
    });

    return () => {
      $('#dataTable tbody').off('click');
      $('#dataTable tbody').off('change');

      if ($.fn.DataTable.isDataTable('#dataTable')) {
        table.destroy();
      }
    };
  }, [filteredJobsData, navigate]);

  const accountOptions = accounts.map((account) => ({
    value: account.id,
    label: `${account.accountTitle} (${account.type}${account.accountNo ? ` - ${account.accountNo}` : ''})`,
  }));

  const accountSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
    menu: (base) => ({ ...base, zIndex: 999999 }),
    control: (base, state) => ({
      ...base,
      minHeight: 44,
      borderRadius: 12,
      borderColor: state.isFocused ? '#111827' : '#e5e7eb',
      boxShadow: state.isFocused ? '0 0 0 4px rgba(17, 24, 39, 0.08)' : 'none',
      '&:hover': { borderColor: '#111827' },
    }),
  };

  const alreadyPaid = paymentLogs.reduce(
    (sum, log) => sum + parseFloat(log.received_amount || 0),
    0
  );
  const totalAmount = parseFloat(selectedInvoice?.amount || selectedInvoice?.totalAmount || 0);
  const remainingBeforeThisPayment = totalAmount - alreadyPaid;
  const newRemaining =
    remainingBeforeThisPayment - parseFloat(receivedAmount || 0);

  return (
    <div className="invoice-list-page">
      <style>
        {`
          .invoice-list-page {
            --invoice-primary: #111827;
            --invoice-muted: #6b7280;
            --invoice-border: #e5e7eb;
            --invoice-soft: #f9fafb;
            --invoice-card: #ffffff;
          }

          .invoice-list-card {
            border: 1px solid var(--invoice-border);
            border-radius: 18px;
            background: var(--invoice-card);
            box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
            overflow: visible;
          }

          .invoice-list-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            padding: 20px 22px;
            border-bottom: 1px solid var(--invoice-border);
          }

          .invoice-list-title {
            margin: 0;
            color: var(--invoice-primary);
            font-size: 18px;
            font-weight: 850;
            letter-spacing: -0.02em;
          }

          .invoice-list-subtitle {
            margin: 4px 0 0;
            color: var(--invoice-muted);
            font-size: 13px;
          }

          .invoice-add-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 42px;
            padding: 10px 16px;
            border-radius: 12px;
            background: var(--invoice-primary);
            color: #fff !important;
            text-decoration: none;
            font-size: 14px;
            font-weight: 800;
            border: 1px solid var(--invoice-primary);
            transition: all .15s ease;
          }

          .invoice-add-btn:hover {
            background: #1f2937;
            transform: translateY(-1px);
          }

          .invoice-datatable-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 14px;
            margin-top: 14px;
          }

          .invoice-search-section {
            padding: 16px 22px 0;
            background: #fff;
          }

          .invoice-search-wrap {
            position: relative;
            max-width: 420px;
          }

          .invoice-search-icon {
            position: absolute;
            left: 14px;
            top: 50%;
            transform: translateY(-50%);
            color: #9ca3af;
            font-size: 16px;
            z-index: 2;
          }

          .invoice-search-input {
            width: 100%;
            min-height: 44px;
            border-radius: 12px;
            border: 1px solid var(--invoice-border);
            padding: 9px 38px 9px 40px;
            color: var(--invoice-primary);
            font-size: 14px;
            font-weight: 650;
            outline: none;
            transition: all .15s ease;
          }

          .invoice-search-input:focus {
            border-color: var(--invoice-primary);
            box-shadow: 0 0 0 4px rgba(17, 24, 39, 0.08);
          }

          .invoice-search-clear {
            position: absolute;
            right: 9px;
            top: 50%;
            transform: translateY(-50%);
            width: 28px;
            height: 28px;
            border-radius: 999px;
            border: 0;
            background: #f3f4f6;
            color: var(--invoice-primary);
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          .invoice-search-meta {
            margin-top: 8px;
            color: var(--invoice-muted);
            font-size: 12px;
            font-weight: 700;
          }

          @media (max-width: 991px) {
            .invoice-search-wrap {
              max-width: 100%;
            }
          }

          .invoice-list-body {
            padding: 0 0 22px;
          }

          .desktop-table-wrap {
            display: block;
            width: 100%;
            overflow: visible;
            padding: 16px 22px 0;
          }

          .mobile-invoice-list {
            display: none;
          }

          .table-responsive-shell {
            width: 100%;
            overflow: visible !important;
            -webkit-overflow-scrolling: touch;
          }

          .invoice-list-page,
          .invoice-list-card,
          .invoice-list-body,
          .desktop-table-wrap,
          .table-responsive-shell,
          .dataTables_wrapper,
          .dataTables_scroll,
          .dataTables_scrollHead,
          .dataTables_scrollBody,
          table.dataTable,
          table.dataTable tbody,
          table.dataTable tr,
          table.dataTable td {
            overflow: visible !important;
          }

          .table-responsive-shell {
            position: relative;
            z-index: 1;
          }

          .table-responsive-shell:has(.dropdown-menu.show),
          .dataTables_wrapper:has(.dropdown-menu.show),
          .invoice-list-card:has(.dropdown-menu.show) {
            z-index: 99999;
          }

          #dataTable tbody tr,
#dataTable tbody td {
  position: relative;
  z-index: 1;
}

#dataTable tbody tr:has(.dropdown-menu.show),
#dataTable tbody td:has(.dropdown-menu.show) {
  position: relative;
  z-index: 1000000 !important;
}

#dataTable tbody tr:has(.dropdown-menu.show) ~ tr,
#dataTable tbody tr:has(.dropdown-menu.show) ~ tr td,
#dataTable tbody tr:has(.dropdown-menu.show) ~ tr .invoice-actions-dropdown {
  z-index: 1 !important;
}

.invoice-actions-dropdown {
  position: relative;
  z-index: 1;
  display: inline-block;
}

.invoice-actions-dropdown:has(.dropdown-menu.show) {
  z-index: 1000000 !important;
}

.invoice-actions-dropdown .dropdown-menu {
  z-index: 1000001;
  border-radius: 12px;
  border: 1px solid var(--invoice-border);
  box-shadow: 0 18px 38px rgba(15, 23, 42, 0.14);
  padding: 6px;
}

.invoice-actions-dropdown .dropdown-menu.show {
  display: block;
  position: absolute !important;
  z-index: 1000001 !important;
  inset: auto 0 auto auto !important;
  transform: translate3d(0, 38px, 0) !important;
  isolation: isolate;
}

.invoice-actions-dropdown.show,
.invoice-actions-dropdown .dropdown-menu.show {
  z-index: 1000001 !important;
}

.invoice-actions-dropdown .dropdown-menu.show * {
  position: relative;
  z-index: 1000002;
}

         

         


          #dataTable {
            width: 100% !important;
            min-width: 920px;
          }

          #dataTable thead th {
            background: #f9fafb;
            color: #374151;
            font-size: 12px;
            font-weight: 850;
            border-bottom: 1px solid var(--invoice-border);
            white-space: nowrap;
          }

       
          .dataTables_wrapper .dataTables_filter input,
          .dataTables_wrapper .dataTables_length select {
            border: 1px solid var(--invoice-border);
            border-radius: 10px;
            min-height: 38px;
            padding: 7px 10px;
            outline: none;
          }

          .dataTables_wrapper .dataTables_filter input:focus,
          .dataTables_wrapper .dataTables_length select:focus {
            border-color: var(--invoice-primary);
            box-shadow: 0 0 0 4px rgba(17, 24, 39, 0.08);
          }


          .invoice-actions-dropdown .dropdown-menu {
            z-index: 999999;
            border-radius: 12px;
            border: 1px solid var(--invoice-border);
            box-shadow: 0 18px 38px rgba(15, 23, 42, 0.14);
            padding: 6px;
          }

          .invoice-actions-dropdown .dropdown-item {
            border-radius: 9px;
            font-size: 13px;
            padding: 9px 11px;
          }

          .invoice-action-toggle {
            border: 1px solid var(--invoice-border);
            background: #fff;
            color: var(--invoice-primary);
            border-radius: 10px;
            min-height: 36px;
            padding: 7px 11px;
            font-size: 13px;
            font-weight: 800;
          }

          .status-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 999px;
            padding: 6px 10px;
            font-size: 12px;
            font-weight: 850;
            line-height: 1;
            white-space: nowrap;
          }

          .status-paid {
            background: #dcfce7;
            color: #166534;
            border: 1px solid #bbf7d0;
          }

          .status-partial {
            background: #fef3c7;
            color: #92400e;
            border: 1px solid #fde68a;
          }

          .status-unpaid {
            background: #fee2e2;
            color: #991b1b;
            border: 1px solid #fecaca;
          }

          .status-default {
            background: #f3f4f6;
            color: #374151;
            border: 1px solid #e5e7eb;
          }

          .invoice-mobile-card {
            border: 1px solid var(--invoice-border);
            border-radius: 16px;
            background: #fff;
            padding: 15px;
            box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
            position: relative;
            z-index: 1;
          }

          .invoice-mobile-card:has(.dropdown-menu.show) {
            z-index: 99999 !important;
          }

          .invoice-mobile-card + .invoice-mobile-card {
            margin-top: 12px;
          }

          .invoice-mobile-top {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            align-items: flex-start;
            margin-bottom: 12px;
          }

          .invoice-mobile-main {
            min-width: 0;
            flex: 1 1 auto;
          }

          .invoice-mobile-number {
            font-size: 15px;
            font-weight: 900;
            color: var(--invoice-primary);
            word-break: break-word;
          }

          .invoice-mobile-customer {
            margin-top: 5px;
            color: var(--invoice-muted);
            font-size: 13px;
            font-weight: 700;
            word-break: break-word;
            min-width: 0;
          }

          .invoice-mobile-status-area {
            display: inline-flex;
            align-items: center;
            justify-content: flex-end;
            gap: 8px;
            flex: 0 0 auto;
          }

          .invoice-detail-toggle {
            width: 30px;
            height: 30px;
            border-radius: 999px;
            border: 1px solid var(--invoice-border);
            background: #f9fafb;
            color: var(--invoice-primary);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
            transition: all 0.18s ease;
            padding: 0;
          }

          .invoice-detail-toggle:hover {
            background: #f3f4f6;
            border-color: #d1d5db;
          }

          .invoice-detail-toggle.is-open {
            background: var(--invoice-primary);
            color: #fff;
            border-color: var(--invoice-primary);
          }

          .invoice-detail-toggle i,
          .invoice-detail-toggle svg {
            transition: transform 0.18s ease;
          }

          .invoice-detail-toggle.is-open i,
          .invoice-detail-toggle.is-open svg {
            transform: rotate(180deg);
          }

          .invoice-mobile-details {
            display: none;
            padding: 12px 0;
            border-top: 1px solid var(--invoice-border);
            border-bottom: 1px solid var(--invoice-border);
            animation: invoiceDetailsSlide 0.18s ease-out;
          }

          .invoice-mobile-details.open {
            display: block;
          }

          .invoice-mobile-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
          }

          @keyframes invoiceDetailsSlide {
            from { opacity: 0; transform: translateY(-4px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .invoice-mobile-label {
            color: var(--invoice-muted);
            font-size: 11px;
            font-weight: 850;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            margin-bottom: 3px;
          }

          .invoice-mobile-value {
            color: var(--invoice-primary);
            font-size: 13px;
            font-weight: 800;
            word-break: break-word;
          }

          .invoice-mobile-footer {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-top: 12px;
          }

          .invoice-mobile-amount-pill {
            display: inline-flex;
            align-items: center;
            min-height: 38px;
            padding: 8px 12px;
            border-radius: 999px;
            background: #f9fafb;
            border: 1px solid var(--invoice-border);
            color: var(--invoice-primary);
            font-size: 13px;
            font-weight: 900;
            white-space: nowrap;
          }

          .invoice-detail-toggle {
            width: 30px;
            height: 30px;
            border-radius: 999px;
            border: 1px solid var(--invoice-border);
            background: #f9fafb;
            color: var(--invoice-primary);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
            transition: all 0.18s ease;
            padding: 0;
            font-size: 16px;
          }

          .invoice-detail-toggle:hover {
            background: #f3f4f6;
            border-color: #d1d5db;
          }

          .invoice-detail-toggle.is-open {
            background: var(--invoice-primary);
            color: #fff;
            border-color: var(--invoice-primary);
          }

          .invoice-detail-toggle i,
          .invoice-detail-toggle svg {
            transition: transform 0.18s ease;
          }

          .invoice-detail-toggle.is-open i,
          .invoice-detail-toggle.is-open svg {
            transform: rotate(180deg);
          }

          .mobile-action-dropdown {
            position: relative;
            flex: 0 0 auto;
          }

          .mobile-action-dropdown:has(.dropdown-menu.show) {
            z-index: 1000000 !important;
          }

          .mobile-action-dropdown .dropdown-menu.show {
            display: block;
            position: absolute !important;
            z-index: 1000001 !important;
            inset: auto 0 auto auto !important;
            transform: translate3d(0, 42px, 0) !important;
          }

          .mobile-action-dropdown .dropdown-menu {
            z-index: 1000001;
            min-width: 215px;
            padding: 6px;
            border: 1px solid var(--invoice-border);
            border-radius: 14px;
            box-shadow: 0 18px 38px rgba(15, 23, 42, 0.16);
          }

          .mobile-action-dropdown .dropdown-item {
            display: flex;
            align-items: center;
            gap: 8px;
            border-radius: 10px;
            padding: 10px 12px;
            color: var(--invoice-primary);
            font-size: 13px;
            font-weight: 800;
          }

          .mobile-action-dropdown .dropdown-item:hover {
            background: #f9fafb;
          }

          .mobile-action-dropdown .dropdown-item.text-success {
            color: #166534 !important;
          }

          .mobile-action-dropdown .dropdown-item.text-danger {
            color: #b91c1c !important;
          }

          .mobile-action-toggle {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            min-height: 40px;
            padding: 9px 13px;
            border-radius: 12px;
            border: 1px solid var(--invoice-primary);
            background: var(--invoice-primary);
            color: #fff;
            font-size: 13px;
            font-weight: 900;
            box-shadow: 0 8px 18px rgba(17, 24, 39, 0.12);
          }

          .payment-modal-backdrop {
            background: rgba(15, 23, 42, 0.55);
            backdrop-filter: blur(2px);
            overflow-y: auto;
            padding: 18px;
          }

          .payment-modal-dialog {
            width: min(860px, 100%);
            margin: 30px auto;
            min-height: calc(100vh - 60px);
            display: flex;
            align-items: center;
          }

          .payment-modal-content {
            width: 100%;
            max-height: calc(100vh - 60px);
            border: 0;
            border-radius: 18px;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
          }

          .payment-modal-header {
            padding: 18px 20px;
            border-bottom: 1px solid var(--invoice-border);
          }

          .payment-modal-title {
            font-size: 17px;
            font-weight: 900;
            color: var(--invoice-primary);
          }

          .payment-modal-body {
            padding: 20px;
            flex: 1 1 auto;
            min-height: 0;
            overflow-y: auto;
          }

          .payment-info-card {
            border: 1px solid var(--invoice-border);
            border-radius: 14px;
            background: #f9fafb;
            padding: 14px;
          }

          .payment-history-shell {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            border: 1px solid var(--invoice-border);
            border-radius: 14px;
          }

          .payment-history-table {
            min-width: 620px;
            margin: 0;
          }

          .payment-history-table th {
            background: #f9fafb;
            color: #374151;
            font-size: 12px;
            white-space: nowrap;
          }

          .payment-history-table td {
            font-size: 13px;
            white-space: nowrap;
            vertical-align: middle;
          }

          .payment-modal-footer {
            padding: 16px 20px;
            border-top: 1px solid var(--invoice-border);
            display: flex;
            justify-content: flex-end;
            gap: 10px;
          }

          .btn-invoice-primary {
            background: var(--invoice-primary) !important;
            border-color: var(--invoice-primary) !important;
            color: #fff !important;
            border-radius: 12px;
            min-height: 42px;
            padding: 9px 16px;
            font-weight: 850;
          }

          .btn-invoice-secondary {
            background: #fff !important;
            color: var(--invoice-primary) !important;
            border: 1px solid var(--invoice-border) !important;
            border-radius: 12px;
            min-height: 42px;
            padding: 9px 16px;
            font-weight: 850;
          }

          @media (max-width: 767.98px) {
            .invoice-list-card {
              border-radius: 14px;
              box-shadow: none;
            }

            .invoice-list-header {
              flex-direction: column;
              align-items: stretch;
              padding: 16px;
            }

            .invoice-add-btn {
              width: 100%;
            }

            .invoice-list-body {
              padding: 14px;
            }

            .desktop-table-wrap {
              display: none;
            }

            .mobile-invoice-list {
              display: block;
            }

            .invoice-mobile-grid {
              grid-template-columns: 1fr;
            }

            .invoice-mobile-top {
              gap: 10px;
            }

            .invoice-mobile-status-area {
              align-items: flex-start;
            }

            .invoice-mobile-top .status-badge {
              max-width: 118px;
              white-space: normal;
              line-height: 1.2;
              text-align: center;
            }

            .invoice-mobile-footer {
              align-items: stretch;
            }

            .invoice-mobile-amount-pill {
              min-width: 0;
              flex: 1 1 auto;
              justify-content: center;
            }

            .mobile-action-dropdown {
              flex: 0 0 auto;
            }

            .mobile-action-toggle {
              min-width: 112px;
            }

            .payment-modal-backdrop {
              padding: 0;
            }

            .payment-modal-dialog {
              width: 100%;
              min-height: 100vh;
              margin: 0;
              display: flex;
              align-items: stretch;
            }

            .payment-modal-content {
              min-height: 100vh;
              max-height: 100vh;
              border-radius: 0;
            }

            .payment-modal-header {
              position: sticky;
              top: 0;
              z-index: 5;
              background: #fff;
              padding: 14px 16px;
            }

            .payment-modal-body {
              flex: 1 1 auto;
              min-height: 0;
              overflow-y: auto;
              padding: 16px;
              padding-bottom: 20px;
            }

            .payment-modal-footer {
              flex: 0 0 auto;
              background: #fff;
              flex-direction: column-reverse;
              padding: 14px 16px;
              box-shadow: 0 -10px 28px rgba(15, 23, 42, 0.08);
            }

            .payment-modal-footer .btn {
              width: 100%;
            }
          }
        `}
      </style>

      <div className="invoice-list-card">
        <div className="invoice-list-header">
          <div>
            <h5 className="invoice-list-title">Order Forms</h5>
            <p className="invoice-list-subtitle">Manage order forms, payments, and customer billing status.</p>
          </div>

          <Link className="invoice-add-btn" to="/create-invoice">
            Add New
          </Link>
        </div>

        <div className="invoice-list-body">
          <div className="invoice-search-section">
            <div className="invoice-search-wrap">
              <i className="ph ph-magnifying-glass invoice-search-icon" />
              <input
                type="text"
                className="invoice-search-input"
                placeholder="Search order forms by number, customer, status, amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="invoice-search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <i className="ph ph-x" />
                </button>
              )}
            </div>
            <div className="invoice-search-meta">
              Showing {filteredJobsData.length} of {jobsData.length} order forms
            </div>
          </div>

          <div className="desktop-table-wrap mt-16">
            <div className="table-responsive-shell">
              <table className="table bordered-table mb-0" id="dataTable">
                <thead>
                  <tr>
                    {/* <th scope="col">ID</th> */}
                    <th scope="col">Order Form No</th>
                    <th scope="col">Customer Name</th>
                    <th scope="col">Issued Date</th>
                    <th scope="col">Due Date</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Status</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody />
              </table>
            </div>
          </div>

          <div className="mobile-invoice-list px-16 pt-16">
            {filteredJobsData.length === 0 ? (
              <div className="text-center text-muted py-4">No order forms found.</div>
            ) : (
              filteredJobsData.map((invoice) => {
                const status = getStatusMeta(invoice.status);

                return (
                  <div className="invoice-mobile-card" key={invoice.id}>
                    <div className="invoice-mobile-top">
                      <div className="invoice-mobile-main">
                        <div className="invoice-mobile-number">{invoice.invoice_number || `Order Form #${invoice.id}`}</div>
                        <div className="invoice-mobile-customer">{invoice.customer_name || 'Unknown Customer'}</div>
                      </div>

                      <div className="invoice-mobile-status-area">
                        <span className={status.className}>{status.label}</span>
                        <button
                          type="button"
                          className={`invoice-detail-toggle ${expandedMobileCards[invoice.id] ? 'is-open' : ''}`}
                          onClick={() => toggleMobileDetails(invoice.id)}
                          aria-expanded={!!expandedMobileCards[invoice.id]}
                          aria-label={expandedMobileCards[invoice.id] ? 'Hide order form details' : 'Show order form details'}
                        >
                          <i className="ph ph-caret-down" />
                        </button>
                      </div>
                    </div>

                    <div className={`invoice-mobile-details ${expandedMobileCards[invoice.id] ? 'open' : ''}`}>
                      <div className="invoice-mobile-grid">
                        <div>
                          <div className="invoice-mobile-label">Issued</div>
                          <div className="invoice-mobile-value">{formatDateString(invoice.issue_date)}</div>
                        </div>

                        <div>
                          <div className="invoice-mobile-label">Due</div>
                          <div className="invoice-mobile-value">{formatDateString(invoice.due_date)}</div>
                        </div>

                        <div>
                          <div className="invoice-mobile-label">ID</div>
                          <div className="invoice-mobile-value">#{invoice.id}</div>
                        </div>

                        <div>
                          <div className="invoice-mobile-label">Status</div>
                          <div className="invoice-mobile-value">{status.label}</div>
                        </div>
                      </div>
                    </div>

                    <div className="invoice-mobile-footer">
                      <div className="invoice-mobile-amount-pill">{formatMoney(invoice)}</div>

                      <div className="dropdown mobile-action-dropdown">
                        <button
                          className="mobile-action-toggle dropdown-toggle"
                          type="button"
                          data-bs-toggle="dropdown"
                          data-bs-boundary="viewport"
                          data-bs-display="dynamic"
                          aria-expanded="false"
                        >
                          Actions
                        </button>
                        <ul className="dropdown-menu dropdown-menu-end">
                          {canRead && (
                            <li>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => navigate(`/invoice-preview/${invoice.id}`)}
                              >
                                <i className="ph ph-eye" /> View Order Form
                              </button>
                            </li>
                          )}

                          {canEdit && (
                            <li>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => navigate(`/invoice-edit/${invoice.id}`)}
                              >
                                <i className="ph ph-pencil" /> Edit Order Form
                              </button>
                            </li>
                          )}

                          {canRead && invoice.status !== 'paid' && (
                            <li>
                              <button
                                type="button"
                                className="dropdown-item text-success"
                                onClick={() => openPaymentModal(invoice)}
                              >
                                <i className="ph ph-credit-card" /> Make Payment
                              </button>
                            </li>
                          )}

                          {canRead && (
                            <li>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => navigate(`/invoice-payments/${invoice.id}`)}
                              >
                                <i className="ph ph-receipt" /> View Payments
                              </button>
                            </li>
                          )}

                          {canDelete && (
                            <li>
                              <button
                                type="button"
                                className="dropdown-item text-danger"
                                onClick={() => handleDelete(invoice.id)}
                              >
                                <i className="ph ph-trash" /> Delete Order Form
                              </button>
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block payment-modal-backdrop" tabIndex="-1">
          <div className="payment-modal-dialog">
            <div className="modal-content payment-modal-content">
              <div className="modal-header payment-modal-header">
                <h5 className="modal-title payment-modal-title">
                  Record Payment
                  <span className="d-block text-muted fw-normal mt-1" style={{ fontSize: 13 }}>
                    Order Form #{selectedInvoice?.invoice_number}
                  </span>
                </h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={closePaymentModal}
                />
              </div>

              <div className="modal-body payment-modal-body">
                {tableError && <div className="alert alert-danger">{tableError}</div>}

                <div className="mb-3">
                  <label className="form-label fw-semibold">Select Account</label>
                  <Select
                    options={accountOptions}
                    value={selectedAccount}
                    onChange={setSelectedAccount}
                    placeholder="Select an account"
                    styles={accountSelectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>

                {selectedAccount && (
                  <div className="payment-info-card mb-3">
                    <h6 className="mb-3">Account Information</h6>
                    {accounts
                      .filter((acc) => acc.id === selectedAccount.value)
                      .map((acc) => (
                        <div key={acc.id} className="row g-2">
                          <div className="col-md-6">
                            <small className="text-muted d-block">Type</small>
                            <strong>{acc.type || '-'}</strong>
                          </div>
                          <div className="col-md-6">
                            <small className="text-muted d-block">Account Title</small>
                            <strong>{acc.accountTitle || '-'}</strong>
                          </div>
                          {acc.accountNo && (
                            <div className="col-md-6">
                              <small className="text-muted d-block">Account Number</small>
                              <strong>{acc.accountNo}</strong>
                            </div>
                          )}
                          {acc.bankName && (
                            <div className="col-md-6">
                              <small className="text-muted d-block">Bank Name</small>
                              <strong>{acc.bankName}</strong>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}

                <h6 className="mt-3 mb-2">Payment History</h6>

                {paymentLogs.length === 0 ? (
                  <div className="payment-info-card mb-3 text-muted">No payment history found.</div>
                ) : (
                  <div className="payment-history-shell mb-3">
                    <table className="table payment-history-table">
                      <thead>
                        <tr>
                          <th>Recorded By</th>
                          <th>Total Amount</th>
                          <th>Received Amount</th>
                          <th>Remaining Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentLogs.map((log, i) => (
                          <tr key={i}>
                            <td>{log?.userName}</td>
                            <td>{log?.total_amount}</td>
                            <td>{log?.received_amount}</td>
                            <td>{log?.remaining_balance}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Total Order Form Amount</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`${selectedInvoice?.currency || ''} ${totalAmount.toFixed(2)}`}
                      readOnly
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Remaining Balance</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`${selectedInvoice?.currency || ''} ${Math.max(newRemaining, 0).toFixed(2)}`}
                      readOnly
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Received Amount</label>
                    <input
                      type="number"
                      className="form-control"
                      value={receivedAmount}
                      onChange={(e) => setReceivedAmount(e.target.value)}
                      placeholder="Enter received amount"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Payment Status</label>
                    <select
                      className="form-select"
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value)}
                    >
                      <option value="CONFIRM">Confirmed</option>
                      <option value="NOT_CONFIRM">Not Confirmed</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="payment-modal-footer">
                <button
                  type="button"
                  className="btn btn-invoice-secondary"
                  onClick={closePaymentModal}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="btn btn-invoice-primary"
                  onClick={handleSavePayment}
                >
                  Save Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceListLayer;
